import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Container, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton,
  List, ListItem, ListItemIcon, ListItemText, Paper, Stack, Typography,
  TextField as MuiTextField,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LockIcon from '@mui/icons-material/Lock';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import StatusBadge from '../../components/shared/StatusBadge';
import EmptyState from '../../components/shared/EmptyState';
import FormProvider from '../../components/form/FormProvider';
import TextField from '../../components/form/TextField';
import PasswordField from '../../components/form/PasswordField';
import { mockBorrowerAccess, mockConditions, mockLoans } from '../../services/mockData';
import { useToast } from '../../components/shared/Toast';

const BORROWER_LOAN_STATUS_MAP = {
  'In Progress': { variant: 'info', label: 'In Progress' },
  Approved: { variant: 'success', label: 'Approved' },
  Submitted: { variant: 'cyan', label: 'Submitted' },
};

const CONDITION_STATUS_MAP = {
  open: { variant: 'warning', label: 'Open' },
  submitted: { variant: 'info', label: 'Submitted' },
  satisfied: { variant: 'success', label: 'Satisfied' },
  rejected: { variant: 'danger', label: 'Rejected' },
};

const accessSchema = z.object({
  loanNumber: z.string().min(1, 'Loan number is required'),
  lastSSN: z.string().min(4, 'Enter the last 4 digits').max(4, 'Enter only 4 digits').regex(/^\d{4}$/, 'Must be 4 digits'),
});

const BorrowerPortalPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const { showToast, ToastContainer } = useToast();
  const [loan, setLoan] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [uploadComment, setUploadComment] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const methods = useForm({
    resolver: zodResolver(accessSchema),
    defaultValues: { loanNumber: '', lastSSN: '' },
  });

  const handleAuthenticate = ({ loanNumber, lastSSN }) => {
    setAuthError('');

    // Find matching borrower access
    const access = mockBorrowerAccess.find(
      a => a.loanNumber === loanNumber && a.borrowerLastSSN === lastSSN
    );

    if (access) {
      // Check if access has expired
      if (new Date(access.expiresAt) < new Date()) {
        setAuthError('This access link has expired. Please contact your loan officer for a new link.');
        return;
      }

      // Load loan and conditions
      const foundLoan = mockLoans.find(l => l.id === access.loanId);
      const borrowerConditions = mockConditions.filter(
        c => c.loanId === access.loanId && c.assignedTo.toLowerCase() === 'borrower'
      );

      setLoan(foundLoan);
      setConditions(borrowerConditions);
      setIsAuthenticated(true);
    } else {
      setAuthError('Invalid loan number or SSN. Please check your information and try again.');
    }
  };

  const handleUploadDocument = () => {
    if (!selectedCondition) return;

    showToast(`Documents uploaded for "${selectedCondition.title}". Your loan officer will be notified.`);

    // Reset form
    setSelectedCondition(null);
    setUploadComment('');
    setUploadedFiles([]);
  };

  const closeUploadModal = () => {
    setSelectedCondition(null);
    setUploadComment('');
    setUploadedFiles([]);
  };

  const handleSignOut = () => {
    setIsAuthenticated(false);
    setLoan(null);
    setConditions([]);
    methods.reset();
  };

  const pendingCount = conditions.filter(c => c.status === 'open' || c.status === 'rejected').length;

  // ── Login Screen ──
  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 3 }}>
        <Box sx={{ maxWidth: 440, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight={700}>Borrower Portal</Typography>
            <Typography variant="body2" color="text.secondary">Upload documents for your loan application</Typography>
          </Box>

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Secure Access</Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600}>Authentication Required</Typography>
                <Typography variant="caption" color="text.secondary">
                  Enter your loan number and the last 4 digits of your Social Security Number to access your loan.
                  This information was provided by your loan officer.
                </Typography>
              </Alert>

              {authError && <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>}

              <FormProvider methods={methods} onSubmit={handleAuthenticate}>
                <TextField
                  name="loanNumber"
                  label="Loan Number"
                  placeholder="e.g., UFF-2024-001"
                  required
                  size="small"
                  helperText="Your loan number was provided in the email from your loan officer"
                  sx={{ mb: 2 }}
                />

                <PasswordField
                  name="lastSSN"
                  label="Last 4 Digits of SSN"
                  placeholder="••••"
                  required
                  size="small"
                  slotProps={{ htmlInput: { maxLength: 4 } }}
                  helperText="Enter only the last 4 digits of your Social Security Number"
                  sx={{ mb: 2 }}
                />

                <Button type="submit" variant="contained" fullWidth startIcon={<LoginIcon />} sx={{ py: 1.2, fontWeight: 600 }}>
                  Access My Loan
                </Button>
              </FormProvider>

              <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2.5, gap: 0.5 }}>
                <LockIcon sx={{ fontSize: 14 }} /> Your information is secure and encrypted
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 2 }}>
            Need help? Contact your loan officer
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Authenticated Dashboard ──
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {ToastContainer}

      {/* Header */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', px: 3, py: 2 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight={700}>Welcome, {loan?.borrower?.firstName}!</Typography>
              <Typography variant="body2" color="text.secondary">
                Loan Number: <strong>{loan?.loanNumber}</strong>
              </Typography>
            </Box>
            <Button variant="outlined" color="inherit" startIcon={<LogoutIcon />} onClick={handleSignOut}>
              Sign Out
            </Button>
          </Stack>
        </Container>
      </Paper>

      {/* Body */}
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Loan Status Card */}
          <Card>
            <CardHeader title="Loan Status" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <StatusBadge status={loan?.status} map={BORROWER_LOAN_STATUS_MAP} />
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Loan Amount</Typography>
                  <Typography variant="h6" fontWeight={700}>${loan?.loanAmount?.toLocaleString()}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Property Address</Typography>
                  <Typography variant="body2" fontWeight={600}>{loan?.property?.address}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {loan?.property?.city}, {loan?.property?.state} {loan?.property?.zipCode}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Loan Officer</Typography>
                  <Typography variant="body2" fontWeight={600}>Michael Chen</Typography>
                  <Typography variant="caption" color="text.secondary">Prime Mortgage Group</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Action Items Card */}
          <Card>
            <CardHeader
              title="Your Action Items"
              subheader={`${pendingCount} item${pendingCount !== 1 ? 's' : ''} need your attention`}
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
              subheaderTypographyProps={{ variant: 'body2' }}
            />
            <Divider />
            <CardContent>
              {conditions.length === 0 ? (
                <EmptyState
                  compact
                  icon={<CheckCircleOutlineIcon color="success" />}
                  title="All caught up!"
                  description="You have no pending action items at this time."
                />
              ) : (
                <List disablePadding>
                  {conditions.map((condition, idx) => (
                    <Box key={condition.id}>
                      {idx > 0 && <Divider />}
                      <ListItem sx={{ py: 2, px: 0, alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <StatusBadge variant={condition.priority === 'High' ? 'danger' : 'warning'}>
                              {condition.priority}
                            </StatusBadge>
                            <Typography variant="body2" fontWeight={600}>{condition.title}</Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{condition.description}</Typography>
                          {condition.dueDate && (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                Due: {new Date(condition.dueDate).toLocaleDateString()}
                              </Typography>
                            </Stack>
                          )}
                          {condition.status === 'rejected' && condition.rejectionReason && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <strong>Rejected:</strong> {condition.rejectionReason}
                            </Alert>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2, flexShrink: 0 }}>
                          <StatusBadge status={condition.status} map={CONDITION_STATUS_MAP} />
                          {(condition.status === 'open' || condition.status === 'rejected') && (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<CloudUploadIcon />}
                              onClick={() => setSelectedCondition(condition)}
                            >
                              Upload
                            </Button>
                          )}
                        </Stack>
                      </ListItem>
                    </Box>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Container>

      {/* Upload Modal */}
      <Dialog open={!!selectedCondition} onClose={closeUploadModal} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent dividers>
          {selectedCondition && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>{selectedCondition.title}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedCondition.description}</Typography>
              </Box>

              <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth>
                Select Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const files = Array.from(e.target.files).map(f => ({
                      name: f.name,
                      size: f.size,
                    }));
                    setUploadedFiles(prev => [...prev, ...files]);
                  }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                You can select multiple files. Accepted formats: PDF, JPG, PNG
              </Typography>

              {uploadedFiles.length > 0 && (
                <List dense disablePadding>
                  {uploadedFiles.map((file, idx) => (
                    <ListItem
                      key={idx}
                      secondaryAction={
                        <IconButton edge="end" size="small" color="error" onClick={() => setUploadedFiles(uploadedFiles.filter((_, i) => i !== idx))}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InsertDriveFileIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={`${Math.round(file.size / 1024)} KB`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              <MuiTextField
                label="Comments (Optional)"
                multiline
                rows={3}
                size="small"
                placeholder="Add any notes or comments about these documents..."
                value={uploadComment}
                onChange={(e) => setUploadComment(e.target.value)}
              />

              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2" fontWeight={600}>Direct to LOS</Typography>
                <Typography variant="caption" color="text.secondary">
                  Your documents will be uploaded directly into the loan origination system
                  and your loan officer will be notified immediately.
                </Typography>
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUploadModal}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleUploadDocument}
            disabled={uploadedFiles.length === 0}
          >
            Upload {uploadedFiles.length} {uploadedFiles.length === 1 ? 'File' : 'Files'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BorrowerPortalPage;
